import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useParams } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { useGetInvoiceDetail } from '../../../hooks/invoice/useGetInvoiceDetail';
import { useInvoiceDetailState } from '../../../hooks/invoice/useInvoiceDetailState';
import { useArchiveInvoices } from '../../../hooks/invoice/useArchiveInvoices';
import { useGetSettings } from '../../../hooks/useGetSettings';
import { useUpdateSettings } from '../../../hooks/useUpdateSettings';
import type { VendorAliasPromptContext } from '../../../modals/invoice/SaveVendorNexAliasPromptModal';
import { normalizeInvoiceVendorAliasKey } from '../../../utils/invoiceVendorDisplay';
import { useRecentActivity } from '../../../hooks/useRecentActivity';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbContext';
import {
    invoicesListHref,
    loadInvoicesViewStateCache,
    readInvoicesViewStateFromLocation,
    withoutInvoicesSearch,
} from '../../../shared/invoicesListViewState';
import type { ZoomImageHighlightBox } from '../../../components/ZoomImage';
import {
    layoutFieldsForTeaching,
    shouldTeachPurchaseOrderLayout,
    useDeleteInvoiceExtractionObservation,
    useGetInvoiceExtractionLayout,
    useRememberInvoiceExtractionObservation,
    useUpdateInvoiceExtractionObservation,
} from '../../../hooks/invoice/useInvoiceExtractionLayout';
import {
    type KeyIdentifierCorrectionCommit,
    type SubmittedIdentifierCorrectionRequest,
    keyIdentifierCorrectionChanged,
    observationFieldForKeyIdentifier,
    shouldRememberTaxExtractionHint,
    submittedIdentifierCorrectionError,
} from '../../../utils/invoiceKeyIdentifierTeaching';
import { filterOutIgnoredTabCalculationMismatches } from '../../../utils/invoiceIgnoredTabFlags';
import type { InvoiceType } from '../../../types/invoice';
import { getInvoiceNexIdentifierUi } from '../../../utils/invoiceNexIdentifierUi';
import { useInvoicePageReplacement } from '../../../hooks/invoice/useInvoicePageReplacement';
import { useInvoiceCollaboration } from '../../../hooks/useInvoiceCollaboration';
import { readInvoicingSettingsFromSections } from './invoiceDetailPageUtils';
import {
    overlayInvoicingSettingsWithVendorRule,
    resolveEffectiveInvoiceFlaggingSettings,
} from '../../../utils/vendorFlaggingRules';
import {
    buildArithmeticFlagMessages,
    computeArithmeticLineIssues,
} from './invoiceDetailArithmetic';
import {
    computeInvoiceDetailSubmitGating,
    createSubmitWithArithmeticGuard,
} from './invoiceDetailSubmitGating';
import {
    filterFlaggedPartsByPolicy,
    omitReturnSlipAmountFlags,
    resolveInvoiceMismatchFlagFamilyPolicy,
} from '../../../utils/invoiceFlagFamilyPolicy';
import { useInvoiceDetailNeighbors } from './useInvoiceDetailNeighbors';

export function useInvoiceDetailPage() {

    const splitPaneRef = useRef<HTMLDivElement | null>(null);
    const [leftPanelWidthPercent, setLeftPanelWidthPercent] = useState(37);
    const [isDraggingSplit, setIsDraggingSplit] = useState(false);
    // Survives the detail view unmounting between previous/next invoices.
    const [rememberedKeyIdentifiersOpen, setRememberedKeyIdentifiersOpen] = useState<boolean | null>(null);
    const { id } = useParams<{ id: string }>();
    const listNavigation = useInvoiceDetailNeighbors(id);
    const location = useLocation();
    // The RO search only rides back on the breadcrumb when the list row click
    // handed it to us; a detail opened any other way restores tab/page only.
    const invoicesViewState = useMemo(
        () => readInvoicesViewStateFromLocation(location.state)
            ?? withoutInvoicesSearch(loadInvoicesViewStateCache()),
        [location.state],
    );
    const queryClient = useQueryClient();
    const { trackView } = useRecentActivity();
    const { data: invoice, isLoading, error, refetch, dataUpdatedAt } = useGetInvoiceDetail(id || '');
    const { data: settings } = useGetSettings();
    const updateSettingsMutation = useUpdateSettings();
    const archiveInvoicesMutation = useArchiveInvoices();
    const toast = useToast();
    const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumbs();
    useInvoiceCollaboration(id);
    const [vendorAliasPrompt, setVendorAliasPrompt] = useState<VendorAliasPromptContext | null>(null);
    const [invoiceImageHighlight, setInvoiceImageHighlight] = useState<{
        region: ZoomImageHighlightBox | null;
        fullWidth: boolean;
    }>({ region: null, fullWidth: false });
    const [isTeachingOpen, setIsTeachingOpen] = useState(false);
    const [hintDrafts, setHintDrafts] = useState<Record<string, string>>({});
    const promptedTeachFieldsRef = useRef<Set<string>>(new Set());
    const teachCorrectionRef = useRef<(commit: KeyIdentifierCorrectionCommit) => void>(() => {});
    const onTeachableAmountCommitted = useCallback((field: 'taxesTotal' | 'orderTotal', value: number) => {
        teachCorrectionRef.current({
            invoiceField: field,
            teachField: field,
            fieldLabel: field === 'taxesTotal' ? 'Taxes' : 'Grand total',
            correctedValue: value.toFixed(2),
        });
    }, []);
    const [teachExtractionPrompt, setTeachExtractionPrompt] = useState<{
        open: boolean;
        commit: KeyIdentifierCorrectionCommit | null;
        extractedValue: string;
    }>({ open: false, commit: null, extractedValue: '' });
    const [submittedIdentifierCorrection, setSubmittedIdentifierCorrection] = useState<{
        request: SubmittedIdentifierCorrectionRequest;
        error: string;
    } | null>(null);
    const [pdfPageCount, setPdfPageCount] = useState<number>(1);
    const [reprocessPagesModalOpen, setReprocessPagesModalOpen] = useState(false);
    const [isImageAdjustPanelExpanded, setIsImageAdjustPanelExpanded] = useState(false);
    const [detailTransitionMode, setDetailTransitionMode] = useState<'reprocess' | 'submit' | null>(null);
    const [isActivityLogOpen, setIsActivityLogOpen] = useState(false);

    const alwaysShowFreightTab = useMemo(() => readInvoicingSettingsFromSections(
        settings?.topSection?.sections,
        settings?.bottomSection?.sections,
    ).freightAsLineItem ?? true, [settings]);

    const salesTaxAsLineItem = useMemo(
        () =>
            readInvoicingSettingsFromSections(
                settings?.topSection?.sections,
                settings?.bottomSection?.sections,
            ).salesTaxAsLineItem === true,
        [settings],
    );

    const unmatchedLineItemsNexSubmitMode = useMemo(
        () =>
            resolveEffectiveInvoiceFlaggingSettings(
                readInvoicingSettingsFromSections(
                    settings?.topSection?.sections,
                    settings?.bottomSection?.sections,
                ),
                invoice?.internalSupplierId,
                invoice?.invoiceType,
            ).unmatchedLineItemsNexSubmitMode,
        [settings, invoice?.internalSupplierId, invoice?.invoiceType],
    );

    const readInvoicingSettingsFromQuery = useCallback(
        () =>
            overlayInvoicingSettingsWithVendorRule(
                readInvoicingSettingsFromSections(
                    settings?.topSection?.sections,
                    settings?.bottomSection?.sections,
                ),
                invoice?.internalSupplierId,
                invoice?.invoiceType,
            ) ??
            readInvoicingSettingsFromSections(
                settings?.topSection?.sections,
                settings?.bottomSection?.sections,
            ),
        [settings, invoice?.internalSupplierId, invoice?.invoiceType],
    );

    const mismatchFlagFamilyPolicy = useMemo(
        () => resolveInvoiceMismatchFlagFamilyPolicy(readInvoicingSettingsFromQuery()),
        [readInvoicingSettingsFromQuery],
    );

    const nexLineQuantityBasis = useMemo(
        () =>
            readInvoicingSettingsFromQuery().nexLineQuantityBasis === 'qtyOnOrder'
                ? ('qtyOnOrder' as const)
                : ('qtyRec' as const),
        [readInvoicingSettingsFromQuery],
    );

    const nexReconciliationIdentifierMode = useMemo(
        () => readInvoicingSettingsFromQuery().nexReconciliationIdentifierMode ?? 'ro',
        [readInvoicingSettingsFromQuery],
    );
    const locationDefaultTaxRates = useMemo(
        () => readInvoicingSettingsFromQuery().locationDefaultTaxRates ?? [],
        [readInvoicingSettingsFromQuery],
    );
    const shopLocations = useMemo(() => {
        const sections = [
            ...(settings?.topSection?.sections ?? []),
            ...(settings?.bottomSection?.sections ?? []),
        ];
        const locationSection = sections.find((section) => section.key === 'location');
        if (locationSection?.key !== 'location') return [];
        return locationSection.data.map((location) => ({
            _id: location._id,
            state: location.state,
        }));
    }, [settings]);
    const identifierUi = useMemo(
        () => getInvoiceNexIdentifierUi(nexReconciliationIdentifierMode),
        [nexReconciliationIdentifierMode]
    );

    const handleAfterVendorMismatchResolved = useCallback(
        (ctx: VendorAliasPromptContext) => {
            const nex = ctx.nexVendorName.trim();
            const inv = ctx.invoiceVendorSnapshot.trim();
            const norm = normalizeInvoiceVendorAliasKey;
            if (!inv || norm(inv) === norm(nex)) return;
            setVendorAliasPrompt(ctx);
        },
        []
    );

    const state = useInvoiceDetailState(id, invoice, {
        queryClient,
        trackView,
        locationDefaultTaxRates,
        shopLocations,
        onAfterVendorMismatchResolved: handleAfterVendorMismatchResolved,
        unmatchedLineItemsNexSubmitMode,
        nexReconciliationIdentifierMode,
        mismatchFlagFamilyPolicy,
        salesTaxAsLineItem,
        onTeachableAmountCommitted,
    });
    const { setCurrentImagePage, updateInvoiceMutation } = state;

    const isReadOnlyForPageReplace =
        invoice?.status === 'submitted' ||
        invoice?.status === 'manually_submitted';
    const pageReplace = useInvoicePageReplacement({
        invoice,
        invoiceId: id,
        isReadOnly: !!isReadOnlyForPageReplace,
    });

    useEffect(() => {
        setInvoiceImageHighlight({ region: null, fullWidth: false });
        setPdfPageCount(1);
        setIsTeachingOpen(false);
        setHintDrafts({});
        setTeachExtractionPrompt({ open: false, commit: null, extractedValue: '' });
        setSubmittedIdentifierCorrection(null);
        promptedTeachFieldsRef.current = new Set();
    }, [id]);

    useEffect(() => {
        if (pageReplace.previewPdfUrl) {
            setCurrentImagePage(1);
        }
    }, [pageReplace.previewPdfUrl, setCurrentImagePage]);

    useEffect(() => {
        if (pageReplace.editMode) {
            setIsImageAdjustPanelExpanded(false);
        }
    }, [pageReplace.editMode]);

    useEffect(() => {
        if (!isDraggingSplit) return undefined;

        const minLeftPercent = 24;
        const maxLeftPercent = 70;
        const updateWidthFromPointer = (clientX: number) => {
            const container = splitPaneRef.current;
            if (!container) return;
            const rect = container.getBoundingClientRect();
            if (rect.width <= 0) return;
            const next = ((clientX - rect.left) / rect.width) * 100;
            const clamped = Math.min(maxLeftPercent, Math.max(minLeftPercent, next));
            setLeftPanelWidthPercent(clamped);
        };
        const onMouseMove = (evt: MouseEvent) => {
            updateWidthFromPointer(evt.clientX);
        };
        const onMouseUp = () => {
            setIsDraggingSplit(false);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [isDraggingSplit]);

    useEffect(() => {
        if (detailTransitionMode !== 'submit') return;
        if (updateInvoiceMutation.isPending) return;
        setDetailTransitionMode(null);
    }, [detailTransitionMode, updateInvoiceMutation.isPending]);

    useEffect(() => {
        const currentPage = invoice?.invoiceNumber
            ? String(invoice.invoiceNumber)
            : undefined;
        const documentTitle = invoice?.invoiceNumber
            ? String(invoice.invoiceNumber)
            : undefined;
        setBreadcrumbs(
            [{
                label: 'Parts Invoicing',
                path: invoicesListHref(invoicesViewState),
                ...(invoicesViewState ? { state: { invoicesViewState } } : {}),
            }],
            currentPage,
            documentTitle,
        );
        return () => clearBreadcrumbs();
    }, [invoice?.invoiceNumber, invoicesViewState, setBreadcrumbs, clearBreadcrumbs]);

    const isReadOnly =
        invoice?.status === 'submitted' ||
        invoice?.status === 'manually_submitted';

    const [isArithmeticBlockOpen, setIsArithmeticBlockOpen] = useState(false);
    const [roChangeConfirmOpen, setRoChangeConfirmOpen] = useState(false);
    const [invoiceTypeChangeModal, setInvoiceTypeChangeModal] = useState<{
        open: boolean;
        pendingType: InvoiceType | null;
    }>({ open: false, pendingType: null });
    const [shopLocationChangeModal, setShopLocationChangeModal] = useState<{
        open: boolean;
        pendingLocationId: string | null;
        pendingLocationTitle: string;
    }>({ open: false, pendingLocationId: null, pendingLocationTitle: '' });
    const [vendorChangeModal, setVendorChangeModal] = useState<{
        open: boolean;
        pendingVendor: string | null;
        source: 'mismatch' | 'suggestion' | null;
    }>({ open: false, pendingVendor: null, source: null });

    const handleInvoiceTypeChangeRequest = (next: InvoiceType) => {
        if (!invoice) return;
        const current = (state.editedFields?.invoiceType ?? invoice.invoiceType ?? 'received') as InvoiceType;
        if (next === current) return;
        setInvoiceTypeChangeModal({ open: true, pendingType: next });
    };

    const handleConfirmInvoiceTypeChange = () => {
        const t = invoiceTypeChangeModal.pendingType;
        if (!t) return;
        setInvoiceTypeChangeModal({ open: false, pendingType: null });
        state.confirmInvoiceTypeChange(t);
    };

    const handleShopLocationChangeRequest = (next: { locationId: string; locationTitle: string }) => {
        if (!invoice) return;
        const currentId = String(state.editedFields?.locationId ?? invoice.locationId ?? '').trim();
        const nextId = String(next.locationId ?? '').trim();
        if (!nextId || nextId === currentId) return;
        setShopLocationChangeModal({
            open: true,
            pendingLocationId: nextId,
            pendingLocationTitle: next.locationTitle ?? '',
        });
    };

    const handleConfirmShopLocationChange = () => {
        const locationId = shopLocationChangeModal.pendingLocationId;
        if (!locationId) return;
        const locationTitle = shopLocationChangeModal.pendingLocationTitle;
        setShopLocationChangeModal({ open: false, pendingLocationId: null, pendingLocationTitle: '' });
        state.confirmShopLocationChange({ locationId, locationTitle });
    };

    const currentVendorForConfirm = () =>
        String(state.editedFields?.supplierText ?? invoice?.supplierText ?? '').trim();

    const handleVendorChangeRequest = (
        nextVendor: string,
        source: 'mismatch' | 'suggestion',
    ) => {
        const next = String(nextVendor ?? '').trim();
        if (!next) return;
        if (next === currentVendorForConfirm()) return;
        setVendorChangeModal({ open: true, pendingVendor: next, source });
    };

    const handleConfirmVendorChange = () => {
        const pending = vendorChangeModal.pendingVendor;
        const source = vendorChangeModal.source;
        setVendorChangeModal({ open: false, pendingVendor: null, source: null });
        if (!pending) return;
        if (source === 'mismatch') {
            state.handleAcceptVendorFromMismatch(pending);
            return;
        }
        if (source === 'suggestion') {
            state.closeVendorSuggestionModal();
            state.confirmVendorChange(pending);
        }
    };

    const isReturnSlip = invoice?.invoiceType === 'return_slip';
    const regularLineItems = state.editedFields?.lineItems ?? invoice?.lineItems ?? [];
    const freightLineItems = state.editedFields?.freightItems ?? invoice?.freightItems ?? [];
    const fuelLineItems = state.editedFields?.fuelItems ?? invoice?.fuelItems ?? [];
    const effectiveLocationId = (() => {
        const v = state.editedFields?.locationId ?? invoice?.locationId;
        return v != null && String(v).trim() !== '' ? String(v).trim() : undefined;
    })();

    const arithmeticLineIssues = useMemo(
        () =>
            computeArithmeticLineIssues({
                localLineArithmeticIssues: state.localLineArithmeticIssues ?? [],
                invoice,
                editedLineItems: state.editedFields?.lineItems,
                editedFreightItems: state.editedFields?.freightItems,
                editedFuelItems: state.editedFields?.fuelItems,
                editedRestockingFeeItems: state.editedFields?.restockingFeeItems,
                editedIgnoredParts: state.editedFields?.ignoredParts,
            }),
        [
            state.localLineArithmeticIssues,
            invoice,
            state.editedFields?.lineItems,
            state.editedFields?.freightItems,
            state.editedFields?.fuelItems,
            state.editedFields?.restockingFeeItems,
            state.editedFields?.ignoredParts,
        ],
    );

    const arithmeticFlagMessages = useMemo(
        () =>
            buildArithmeticFlagMessages({
                arithmeticLineIssues,
                lineItems: state.editedFields?.lineItems ?? invoice?.lineItems ?? [],
                freightItems: state.editedFields?.freightItems ?? invoice?.freightItems ?? [],
                fuelItems: state.editedFields?.fuelItems ?? invoice?.fuelItems ?? [],
                restockingFeeItems:
                    state.editedFields?.restockingFeeItems ?? invoice?.restockingFeeItems ?? [],
                discountItems: state.editedFields?.discountItems ?? invoice?.discountItems ?? [],
                ignoredParts: state.editedFields?.ignoredParts ?? invoice?.ignoredParts ?? [],
                localGrandTotalVsAboveLineItemsWithFeesMismatch:
                    state.localGrandTotalVsAboveLineItemsWithFeesMismatch ?? null,
            }),
        [
            arithmeticLineIssues,
            state.editedFields?.lineItems,
            state.editedFields?.freightItems,
            state.editedFields?.fuelItems,
            state.editedFields?.restockingFeeItems,
            state.editedFields?.discountItems,
            state.editedFields?.ignoredParts,
            invoice,
            state.localGrandTotalVsAboveLineItemsWithFeesMismatch,
        ],
    );

    const hasArithmeticFlags = arithmeticFlagMessages.length > 0;

    const submitArithmeticHelpText = useMemo(() => {
        if (!hasArithmeticFlags) return undefined;
        const fix =
            'Please fix the arithmetic issues below. Submit turns on as soon as every arithmetic check passes.';
        const details =
            arithmeticFlagMessages.length > 0
                ? `\n\nRight now:\n${arithmeticFlagMessages.slice(0, 8).map((m) => `• ${m}`).join('\n')}${arithmeticFlagMessages.length > 8 ? `\n• …and ${arithmeticFlagMessages.length - 8} more` : ''}`
                : '';
        return `${fix}${details}`;
    }, [hasArithmeticFlags, arithmeticFlagMessages]);

    const visibleServerFlaggedPartsRaw = filterFlaggedPartsByPolicy(
        filterOutIgnoredTabCalculationMismatches(
            invoice?.reconciliationIssues?.flaggedParts,
            {
                lineItems: state.editedFields?.lineItems ?? invoice?.lineItems ?? [],
                freightItems: state.editedFields?.freightItems ?? invoice?.freightItems ?? [],
                fuelItems: state.editedFields?.fuelItems ?? invoice?.fuelItems ?? [],
                restockingFeeItems:
                    state.editedFields?.restockingFeeItems ?? invoice?.restockingFeeItems ?? [],
                ignoredParts: state.editedFields?.ignoredParts ?? invoice?.ignoredParts ?? [],
            },
        ),
        mismatchFlagFamilyPolicy,
    );
    const visibleServerFlaggedParts = omitReturnSlipAmountFlags(
        invoice?.invoiceType === 'return_slip'
            ? visibleServerFlaggedPartsRaw.filter(
                  (i: { issueType?: string }) => i.issueType !== 'calculation_mismatch',
              )
            : visibleServerFlaggedPartsRaw,
        invoice?.invoiceType,
    );

    const serverReconciliationIssues = !!(
        invoice?.reconciliationIssues &&
        (visibleServerFlaggedParts.length > 0 ||
            invoice.reconciliationIssues.vendorNameMismatch ||
            invoice.reconciliationIssues.specialVendorMatch ||
            invoice.reconciliationIssues.uploaderLocationMismatch ||
            invoice.reconciliationIssues.staleNexFolder ||
            invoice.reconciliationIssues.roNumberNotFound ||
            (invoice.reconciliationIssues.locationNotResolved && !effectiveLocationId) ||
            invoice.reconciliationIssues.totalFormulaMismatch ||
            invoice.reconciliationIssues.extractedGrandTotalVsAboveLineItemsWithFeesMismatch ||
            invoice.reconciliationIssues.noExtractedLineItems ||
            invoice.reconciliationIssues.nexSubmissionError)
    );
    const hasReconciliationIssues =
        serverReconciliationIssues ||
        (!isReadOnly && invoice?.invoiceType !== 'return_slip' && hasArithmeticFlags);

    const hasMatchNotes =
        (state.effectiveReconciliationMatchNotes ??
            invoice?.reconciliationIssues?.reconciliationMatchNotes ??
            []
        ).length > 0 ||
        (invoice?.reconciliationIssues?.rejectedInReturnsParts ?? []).length > 0;

    const hasSubmittedNexPayload =
        (invoice?.status === 'submitted' || invoice?.status === 'manually_submitted') &&
        invoice?.nexSubmission?.lastStatus === 'succeeded';

    const hasReconciliationsContent =
        hasReconciliationIssues || hasMatchNotes || hasSubmittedNexPayload;

    const handleConfirmRoChangeSave = () => {
        setRoChangeConfirmOpen(false);
        state.handleSave({ refreshNexsyisData: true });
    };

    const nexSubmissionErrorDetail =
        invoice?.nexSubmission?.lastStatus === 'failed'
            ? invoice.nexSubmission.lastError?.detail ??
              invoice.nexSubmission.lastError?.title ??
              'Failed to submit invoice to NEX. Please review the invoice and try again.'
            : null;

    const derivedReasonFlags = useMemo(() => {
        const ri = invoice?.reconciliationIssues;
        if (!ri) return undefined;
        const issueTypes = new Set((ri.flaggedParts ?? []).map((p: { issueType: string }) => p.issueType));
        const serverMathFlag =
            !!ri.totalFormulaMismatch ||
            !!ri.extractedGrandTotalVsAboveLineItemsWithFeesMismatch ||
            !!ri.noExtractedLineItems ||
            issueTypes.has('calculation_mismatch');
        // Keep the MATH badge in sync with live submit gating:
        // while editing, use local arithmetic state so the badge clears immediately.
        const liveMathFlag =
            !isReadOnly && invoice?.invoiceType !== 'return_slip'
                ? hasArithmeticFlags
                : serverMathFlag;
        return {
            location: !!ri.locationNotResolved && !effectiveLocationId,
            ro: !!ri.roNumberNotFound,
            nex: issueTypes.has('missing_in_nex') || issueTypes.has('different_part_names') || issueTypes.has('different_positions') || issueTypes.has('differing_qualifiers') || issueTypes.has('part_number_changed'),
            math: liveMathFlag,
            dupe:
                ri.nexSubmissionError?.status === 406 ||
                (invoice?.invoiceType !== 'return_slip' && !!ri.partbayDuplicate),
            vendor: !!ri.vendorNameMismatch,
            specialVendor: !!ri.specialVendorMatch,
            locationMismatch: !!ri.uploaderLocationMismatch,
            stale: !!ri.staleNexFolder,
            price:
                mismatchFlagFamilyPolicy.priceMismatchFlaggingEnabled &&
                (issueTypes.has('price_mismatch') ||
                    issueTypes.has('list_price_mismatch') ||
                    issueTypes.has('list_price_equals_buy_price')),
            discount: issueTypes.has('discount_below_expected'),
            qty:
                mismatchFlagFamilyPolicy.quantityMismatchFlaggingEnabled &&
                issueTypes.has('quantity_mismatch'),
            total:
                invoice?.invoiceType !== 'return_slip' &&
                mismatchFlagFamilyPolicy.amountMismatchFlaggingEnabled &&
                (issueTypes.has('amount_mismatch') ||
                    issueTypes.has('amount_mismatch_multiple_nex_lines') ||
                    issueTypes.has('amount_mismatch_single_nex_line') ||
                    issueTypes.has('amount_mismatch_nex_structure')),
        };
    }, [
        invoice?.reconciliationIssues,
        invoice?.invoiceType,
        isReadOnly,
        hasArithmeticFlags,
        effectiveLocationId,
        mismatchFlagFamilyPolicy,
    ]);

    const derivedReasonCodes = useMemo(() => {
        const status = invoice?.reconciliationIssues?.nexSubmissionError?.status;
        if (status && status !== 406) return [status];
        return undefined;
    }, [invoice?.reconciliationIssues]);

    const allImages =
        pageReplace.previewImageUrls ??
        (invoice?.images?.length ? invoice.images : invoice?.image ? [invoice.image] : []);
    const primaryImageSrc = invoice?.image ?? allImages[0] ?? null;
    const isPdf =
        invoice?.imageContentType === 'application/pdf' ||
        (primaryImageSrc?.startsWith('data:application/pdf') ?? false);
    const imageAddAccept = useMemo(() => {
        const kind = pageReplace.rasterKind;
        if (kind === 'jpeg' || kind === 'tiff') return 'image/jpeg,.jpg,.jpeg,image/tiff,.tif,.tiff';
        if (kind === 'png') return 'image/png,.png';
        if (kind === 'webp') return 'image/webp,.webp';
        return 'image/jpeg,image/png,image/webp,image/tiff,.jpg,.jpeg,.png,.webp,.tif,.tiff';
    }, [pageReplace.rasterKind]);
    const currentImageSrc =
        pageReplace.editMode && isPdf && !pageReplace.previewPdfUrl
            ? null
            : pageReplace.previewPdfUrl ??
              (isPdf ? invoice?.image ?? allImages[0] ?? null : allImages.length > 0 ? allImages[state.currentImagePage - 1] ?? null : null);

    useEffect(() => {
        if (!isPdf) return;
        if (state.currentImagePage > pdfPageCount) {
            state.setCurrentImagePage(pdfPageCount);
        }
    }, [isPdf, pdfPageCount, state]);

    useEffect(() => {
        if (isPdf) return;
        const n = allImages.length;
        if (n > 0 && state.currentImagePage > n) {
            state.setCurrentImagePage(n);
        }
    }, [allImages.length, isPdf, state]);


    const layoutQuery = useGetInvoiceExtractionLayout(id || '', isTeachingOpen || teachExtractionPrompt.open);
    const rememberObservationMutation = useRememberInvoiceExtractionObservation();
    const updateObservationMutation = useUpdateInvoiceExtractionObservation();
    const deleteObservationMutation = useDeleteInvoiceExtractionObservation();

    const invoiceTypeForLayout = (state.editedFields?.invoiceType ?? invoice?.invoiceType ?? '') as InvoiceType | '';
    const includePurchaseOrder = shouldTeachPurchaseOrderLayout({
        invoiceType: invoiceTypeForLayout || null,
        nexReconciliationIdentifierMode,
    });
    const availableTeachFields = layoutFieldsForTeaching(includePurchaseOrder);

    useEffect(() => {
        if (!layoutQuery.data?.observations) return;
        const nextDrafts: Record<string, string> = {};
        for (const obs of layoutQuery.data.observations) {
            nextDrafts[obs.id] = obs.enhancedHint;
        }
        setHintDrafts(nextDrafts);
    }, [layoutQuery.data?.observations]);

    const openExtractionTeaching = useCallback(() => {
        setIsTeachingOpen(true);
    }, []);

    const handleKeyIdentifierCorrectionCommitted = useCallback((commit: KeyIdentifierCorrectionCommit) => {
        if (isReadOnly) return;
        const observationField = observationFieldForKeyIdentifier(commit.invoiceField);
        if (!observationField) return;
        if (commit.invoiceField === 'taxesTotal') {
            const locationId = String(state.editedFields?.locationId ?? invoice?.locationId ?? '');
            const locationState = shopLocations.find((location) => location._id === locationId)?.state;
            const nexTaxFormat = state.editedFields?.nexTaxFormat ?? invoice?.nexTaxFormat;
            if (!shouldRememberTaxExtractionHint({ nexTaxFormat, locationState })) return;
        }
        const promptKey = `${commit.invoiceField}:${commit.teachField}`;
        if (promptedTeachFieldsRef.current.has(promptKey)) return;
        if (layoutQuery.data?.canEdit === false) return;

        const original = state.getOriginalValue(commit.invoiceField);
        const invoiceType = state.editedFields?.invoiceType ?? invoice?.invoiceType;
        const moneyByMagnitude =
            (commit.invoiceField === 'taxesTotal' || commit.invoiceField === 'orderTotal') &&
            (invoiceType === 'returned' || invoiceType === 'return_slip');
        if (!keyIdentifierCorrectionChanged(commit, original, { moneyByMagnitude })) return;

        const extractedValue = commit.invoiceField === 'timestamp' && original instanceof Date
            ? `${String(original.getMonth() + 1).padStart(2, '0')}/${String(original.getDate()).padStart(2, '0')}/${original.getFullYear()}`
            : (commit.invoiceField === 'taxesTotal' || commit.invoiceField === 'orderTotal') && typeof original === 'number' && Number.isFinite(original)
                ? (moneyByMagnitude ? Math.abs(original) : original).toFixed(2)
                : String(original ?? '').trim();

        setTeachExtractionPrompt({ open: true, commit, extractedValue });
    }, [isReadOnly, invoice?.invoiceType, invoice?.locationId, invoice?.nexTaxFormat, layoutQuery.data?.canEdit, shopLocations, state]);
    teachCorrectionRef.current = handleKeyIdentifierCorrectionCommitted;

    const handleCloseTeachExtractionPrompt = useCallback(() => {
        const commit = teachExtractionPrompt.commit;
        if (commit) {
            promptedTeachFieldsRef.current.add(`${commit.invoiceField}:${commit.teachField}`);
        }
        setTeachExtractionPrompt({ open: false, commit: null, extractedValue: '' });
    }, [teachExtractionPrompt.commit]);

    const handleConfirmTeachExtractionPrompt = useCallback(() => {
        const commit = teachExtractionPrompt.commit;
        const extractedValue = teachExtractionPrompt.extractedValue;
        if (!commit || !id) return;
        const observationField = observationFieldForKeyIdentifier(commit.invoiceField);
        if (!observationField) return;
        const promptKey = `${commit.invoiceField}:${commit.teachField}`;
        promptedTeachFieldsRef.current.add(promptKey);
        setTeachExtractionPrompt({ open: false, commit: null, extractedValue: '' });

        rememberObservationMutation.mutate(
            {
                invoiceId: id,
                field: observationField,
                corrected: commit.correctedValue,
            },
            {
                onSuccess: (data) => {
                    if ('status' in data && data.status === 'processing') return;
                    toast({
                        title: 'AI extraction hint saved',
                        description: 'Future invoices from this vendor will use this correction.',
                        status: 'success',
                        duration: 4500,
                        isClosable: true,
                    });
                },
                onError: (error: unknown) => {
                    promptedTeachFieldsRef.current.delete(promptKey);
                    setTeachExtractionPrompt({ open: true, commit, extractedValue });
                    const axiosError = error as { response?: { data?: { message?: string } } };
                    toast({
                        title: 'Could not save hint',
                        description: axiosError.response?.data?.message ?? 'Something went wrong while analyzing the invoice.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    }, [id, rememberObservationMutation, teachExtractionPrompt.commit, teachExtractionPrompt.extractedValue, toast]);

    const openSubmittedIdentifierCorrection = useCallback((request: SubmittedIdentifierCorrectionRequest) => {
        setSubmittedIdentifierCorrection({ request, error: '' });
    }, []);

    const closeSubmittedIdentifierCorrection = useCallback(() => {
        if (rememberObservationMutation.isPending) return;
        setSubmittedIdentifierCorrection(null);
    }, [rememberObservationMutation.isPending]);

    const handleConfirmSubmittedIdentifierCorrection = useCallback((correctedValue: string) => {
        const request = submittedIdentifierCorrection?.request;
        if (!request || !id) return;
        const validationError = submittedIdentifierCorrectionError({
            sourceField: request.sourceField,
            submittedValue: request.submittedValue,
            correctedValue,
            original: state.getOriginalValue(request.sourceField),
        });
        if (validationError) {
            setSubmittedIdentifierCorrection({ request, error: validationError });
            return;
        }
        const observationField = observationFieldForKeyIdentifier(request.sourceField);
        if (!observationField) return;

        rememberObservationMutation.mutate(
            {
                invoiceId: id,
                field: observationField,
                corrected: correctedValue.trim(),
            },
            {
                onSuccess: (data) => {
                    setSubmittedIdentifierCorrection(null);
                    if ('status' in data && data.status === 'processing') return;
                    toast({
                        title: 'AI extraction hint saved',
                        description: 'Future invoices from this vendor will use this correction.',
                        status: 'success',
                        duration: 4500,
                        isClosable: true,
                    });
                },
                onError: (error: unknown) => {
                    const axiosError = error as { response?: { data?: { message?: string } } };
                    const message = axiosError.response?.data?.message ?? 'Something went wrong while analyzing the invoice.';
                    setSubmittedIdentifierCorrection({ request, error: message });
                },
            },
        );
    }, [id, rememberObservationMutation, state, submittedIdentifierCorrection?.request, toast]);

    const closeExtractionTeaching = useCallback(() => {
        setIsTeachingOpen(false);
    }, []);

    const handleSaveObservationHint = useCallback((observationId: string, enhancedHint: string) => {
        if (!id) return;
        updateObservationMutation.mutate(
            { invoiceId: id, observationId, enhancedHint },
            {
                onSuccess: () => {
                    toast({
                        title: 'Hint updated',
                        status: 'success',
                        duration: 2500,
                        isClosable: true,
                    });
                },
                onError: (error: unknown) => {
                    const axiosError = error as { response?: { data?: { message?: string } } };
                    toast({
                        title: 'Could not update hint',
                        description: axiosError.response?.data?.message ?? 'Please try again.',
                        status: 'error',
                        duration: 4000,
                        isClosable: true,
                    });
                },
            },
        );
    }, [id, toast, updateObservationMutation]);

    const handleDeleteObservation = useCallback((observationId: string) => {
        if (!id) return;
        deleteObservationMutation.mutate(
            { invoiceId: id, observationId },
            {
                onSuccess: () => {
                    toast({
                        title: 'Hint removed',
                        status: 'success',
                        duration: 2500,
                        isClosable: true,
                    });
                },
                onError: () => {
                    toast({
                        title: 'Could not remove hint',
                        status: 'error',
                        duration: 4000,
                        isClosable: true,
                    });
                },
            },
        );
    }, [deleteObservationMutation, id, toast]);

    const invoiceTypeLabel =
        invoiceTypeForLayout === 'received' ? 'received'
            : invoiceTypeForLayout === 'returned' ? 'credited'
                : invoiceTypeForLayout === 'return_slip' ? 'return slip'
                    : invoiceTypeForLayout === 'sublet' ? 'sublet'
                        : invoiceTypeForLayout || 'this type';

    const extractionTeaching = {
        identifierFieldTitle: identifierUi.keyIdentifiersFieldTitle,
        vendorName: currentVendorForConfirm() || 'this vendor',
        invoiceTypeLabel,
        includePurchaseOrder,
        observations: layoutQuery.data?.observations ?? [],
        isLoading: isTeachingOpen && layoutQuery.isLoading,
        loadFailed: isTeachingOpen && layoutQuery.isError,
        onRetryLoad: () => { layoutQuery.refetch(); },
        isSaving: rememberObservationMutation.isPending
            || updateObservationMutation.isPending
            || deleteObservationMutation.isPending,
        canEdit: layoutQuery.data?.canEdit !== false,
        onBack: closeExtractionTeaching,
        onSaveHint: handleSaveObservationHint,
        onDeleteObservation: handleDeleteObservation,
        draftHints: hintDrafts,
        onDraftHintChange: (observationId: string, value: string) => {
            setHintDrafts((prev) => ({ ...prev, [observationId]: value }));
        },
    };

    const submitGating = invoice
        ? computeInvoiceDetailSubmitGating({
        invoice,
        isReturnSlip,
        hasArithmeticFlags,
        returnSlipSubmitReady: state.returnSlipSubmitReady,
        returnSlipSubmitPreview: state.returnSlipSubmitPreview,
        identifierUi,
        submitArithmeticHelpText,
        regularLineItems,
        freightLineItems,
        fuelLineItems,
        effectiveMatchedNexLines: state.effectiveMatchedNexLines ?? [],
        localMatchedNexOverrides: state.localMatchedNexOverrides ?? {},
        unmatchedPartIndices: state.unmatchedPartIndices ?? new Set(),
        remapSubmitPreviewPartIndex: state.remapSubmitPreviewPartIndex,
        effectiveLocationId,
        unmatchedLineItemsNexSubmitMode,
    })
        : {
              missingRequiredNexMatchesForSubmit: false,
              missingNexMatchSubmitHelpText: undefined,
              returnSlipSubmitBlocked: true,
              locationBlocksSubmit: false,
              arithmeticBlocksSubmit: false,
              duplicate406BlocksSubmit: false,
              nexMatchBlocksSubmit: false,
              submitDisabledHelpCombined: undefined,
          };

    const handleSubmitWithArithmeticGuard = createSubmitWithArithmeticGuard(
        isReturnSlip,
        hasArithmeticFlags,
    );

    const handleToggleArchive = useCallback(async () => {
        if (!id || !invoice) return;
        const nextArchived = !invoice.archived;
        try {
            await archiveInvoicesMutation.mutateAsync({
                invoiceId: id,
                archived: nextArchived,
            });
            toast({
                status: 'success',
                title: nextArchived ? 'Invoice archived' : 'Invoice restored',
                duration: 2000,
                isClosable: true,
            });
        } catch {
            toast({
                status: 'error',
                title: 'Failed to update archive state',
                duration: 2500,
                isClosable: true,
            });
        }
    }, [id, invoice, archiveInvoicesMutation, toast]);

    return {
        id,
        listNavigation,
        rememberedKeyIdentifiersOpen,
        setRememberedKeyIdentifiersOpen,
        invoice,
        isLoading,
        error,
        refetch,
        dataUpdatedAt,
        showLoadingSkeleton: isLoading || detailTransitionMode != null,
        state,
        pageReplace,
        toast,
        queryClient,
        updateSettingsMutation,
        settings,
        splitPaneRef,
        leftPanelWidthPercent,
        isDraggingSplit,
        setIsDraggingSplit,
        vendorAliasPrompt,
        setVendorAliasPrompt,
        invoiceImageHighlight,
        setInvoiceImageHighlight,
        pdfPageCount,
        setPdfPageCount,
        reprocessPagesModalOpen,
        setReprocessPagesModalOpen,
        isImageAdjustPanelExpanded,
        setIsImageAdjustPanelExpanded,
        detailTransitionMode,
        setDetailTransitionMode,
        alwaysShowFreightTab,
        salesTaxAsLineItem,
        mismatchFlagFamilyPolicy,
        nexLineQuantityBasis,
        unmatchedLineItemsNexSubmitMode,
        nexReconciliationIdentifierMode,
        identifierUi,
        readInvoicingSettingsFromQuery,
        isReadOnly,
        isReturnSlip,
        isPdf,
        allImages,
        currentImageSrc,
        imageAddAccept,
        primaryImageSrc,
        arithmeticFlagMessages,
        hasArithmeticFlags,
        submitGating,
        handleSubmitWithArithmeticGuard,
        handleToggleArchive,
        isArchiving: archiveInvoicesMutation.isPending,
        handleConfirmRoChangeSave,
        handleInvoiceTypeChangeRequest,
        handleConfirmInvoiceTypeChange,
        handleShopLocationChangeRequest,
        handleConfirmShopLocationChange,
        handleVendorChangeRequest,
        handleConfirmVendorChange,
        derivedReasonFlags,
        derivedReasonCodes,
        nexSubmissionErrorDetail,
        hasReconciliationIssues,
        hasReconciliationsContent,
        isArithmeticBlockOpen,
        setIsArithmeticBlockOpen,
        roChangeConfirmOpen,
        setRoChangeConfirmOpen,
        invoiceTypeChangeModal,
        setInvoiceTypeChangeModal,
        shopLocationChangeModal,
        setShopLocationChangeModal,
        vendorChangeModal,
        setVendorChangeModal,
        visibleServerFlaggedPartsRaw: visibleServerFlaggedParts,
        isActivityLogOpen,
        setIsActivityLogOpen,
        extractionTeaching,
        isTeachingOpen,
        openExtractionTeaching,
        handleKeyIdentifierCorrectionCommitted,
        teachExtractionPrompt,
        handleCloseTeachExtractionPrompt,
        handleConfirmTeachExtractionPrompt,
        submittedIdentifierCorrection,
        openSubmittedIdentifierCorrection,
        closeSubmittedIdentifierCorrection,
        handleConfirmSubmittedIdentifierCorrection,
        rememberObservationPending: rememberObservationMutation.isPending,
        availableTeachFields,
    };
}
